import React, { useEffect, useState } from 'react';
import ClickUpModalRouter from './ClickUpModalRouter';
import './ClickUpModal.css';

interface ClickUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialView?: 'inbox' | 'tasks' | 'task-detail';
  initialTaskId?: string;
}

const ClickUpModal: React.FC<ClickUpModalProps> = ({
  isOpen,
  onClose,
  initialView = 'inbox',
  initialTaskId,
}) => {
  const [currentView, setCurrentView] = useState(initialView);
  const [currentTaskId, setCurrentTaskId] = useState(initialTaskId);

  // Sync with initial props
  useEffect(() => {
    if (isOpen) {
      setCurrentView(initialView);
      setCurrentTaskId(initialTaskId);
    }
  }, [isOpen, initialView, initialTaskId]);

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Block body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Internal navigation handlers
  const navigateToInbox = () => {
    setCurrentView('inbox');
    setCurrentTaskId(undefined);
  };

  const navigateToTasks = () => {
    setCurrentView('tasks');
    setCurrentTaskId(undefined);
  };

  const navigateToTask = (taskId: string) => {
    setCurrentTaskId(taskId);
    setCurrentView('task-detail');
  };

  if (!isOpen) return null;

  return (
    <div className="clickup-modal-overlay" onClick={onClose}>
      <div 
        className="clickup-modal-container" 
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          className="clickup-modal-close" 
          onClick={onClose}
          aria-label="Закрыть"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="clickup-modal-content">
          <ClickUpModalRouter
            currentView={currentView}
            currentTaskId={currentTaskId}
            onNavigateToInbox={navigateToInbox}
            onNavigateToTasks={navigateToTasks}
            onNavigateToTask={navigateToTask}
          />
        </div>
      </div>
    </div>
  );
};

export default ClickUpModal;
